import {
  ActionIcon,
  Button,
  Center,
  ComboboxItem,
  Divider,
  Group,
  Loader,
  Pagination,
  Select,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconDownload } from "@tabler/icons-react";
import { safeApiCall } from "api/common.service";
import {
  getSalesAnalysisByMonth,
  getSalesAnalysisByQuarter,
} from "api/report.service";
import {
  MantineNotificationColor,
  MantineNotificationPosition,
} from "common/constants";
import { navigateTo } from "common/navigation";
import ProductCodeInput from "pages/admin/components/ProductCodeInput";
import SalesAnalysisReportTypeSelection from "pages/admin/components/SalesAnalysisReportTypeSelection";
import YearSelect from "pages/admin/components/YearSelection";
import React, { useEffect, useRef, useState } from "react";
import {
  RprtSalesAnalysisByQuarters,
  RptSalesAnalysisByMonths,
} from "types/model.types";
import SalesAnalysisView from "./SalesAnalysisView";
import SalesAveragesView from "./SalesAveragesView";

const AdminSalesAnalysisReportPage: React.FC = () => {
  const [data, setData] = useState<
    RptSalesAnalysisByMonths | RprtSalesAnalysisByQuarters | null
  >(null);
  const [loading, setLoading] = useState(false);
  const [fromCode, setFromCode] = useState<number | undefined>();
  const [toCode, setToCode] = useState<number | undefined>();
  const [selectedYear, setSelectedYear] = useState(
    new Date().getFullYear().toString(),
  );
  const apiFetchRef = useRef(false);
  const [pageSize, setPageSize] = useState(50);
  const [pageNumber, setPageNumber] = useState(1);
  const [total, setTotal] = useState(0);
  const [reportType, setReportType] = useState<number>(0);

  //Clear data on type switch
  useEffect(() => {
    setData(null); // reset when switching report type
    fetchData();
  }, [reportType]);

  const fetchData = async (download: boolean | null = false) => {
    if (apiFetchRef.current) return;
    apiFetchRef.current = true;
    setLoading(true);
    const startCode = fromCode ?? null;
    const endCode = toCode ?? null;

    if (download) {
      try {
        switch (reportType) {
          case 0:
            await safeApiCall(
              getSalesAnalysisByMonth(
                Number(selectedYear),
                startCode,
                endCode,
                null,
                null,
                true,
              ),
            );
            break;
          default:
            await safeApiCall(
              getSalesAnalysisByQuarter(
                Number(selectedYear),
                startCode,
                endCode,
                null,
                null,
                true,
              ),
            );
            break;
        }
        notifications.show({
          title: "File Download!",
          message: `Downloading CSV report`,
          color: MantineNotificationColor.Success,
          position: MantineNotificationPosition.TopCenter,
        });
      } catch (err) {
        notifications.show({
          title: "File Download!",
          message: `Unable to export CSV for this report. ${err}`,
          color: MantineNotificationColor.Error,
          position: MantineNotificationPosition.TopCenter,
        });
      }
    } else {
      let result;

      switch (reportType) {
        case 0:
          result = await safeApiCall(
            getSalesAnalysisByMonth(
              Number(selectedYear),
              startCode,
              endCode,
              pageSize,
              pageNumber,
            ),
          );
          break;
        default:
          result = await safeApiCall(
            getSalesAnalysisByQuarter(
              Number(selectedYear),
              startCode,
              endCode,
              pageSize,
              pageNumber,
            ),
          );
          break;
      }
      setData(result?.data ?? []);
      setTotal(result?.total ?? 0);
    }

    setLoading(false);
    apiFetchRef.current = false;
  };

  useEffect(() => {
    fetchData();
  }, [pageNumber, pageSize, selectedYear]);
  const handleYearChange = (value: string | null, _option: ComboboxItem) => {
    if (value) setSelectedYear(value);
  };
  return (
    <Stack>
      <Group justify="space-between" align="end" gap="sm">
        <Title order={2}>Sales Analysis Report</Title>
        <Button
          onClick={() => {
            navigateTo(
              `/print/sales-analysis?rpt=${reportType}&y=${selectedYear}&f=${fromCode ?? ""}&t=${toCode ?? ""}`,
            );
          }}
          variant="gradient"
        >
          Print
        </Button>
      </Group>

      <Group align="end" gap="sm">
        <SalesAnalysisReportTypeSelection
          name="SalesAnalysisReportType"
          label="Choose Report Type"
          title="Defaults to product sales analysis"
          value={reportType}
          setValue={setReportType}
        />
        <YearSelect
          value={selectedYear}
          onChange={handleYearChange}
          title="Defaults to current year if blank"
        />
        <ProductCodeInput
          name="fromCode"
          label="From Product Code"
          value={fromCode}
          setValue={setFromCode}
          resetPage={() => setPageNumber(1)}
          maxLength={6} // e.g. max 6 digits
          min={0}
          max={999999}
        />
        <ProductCodeInput
          name="toCode"
          label="To Product Code"
          value={toCode}
          setValue={setToCode}
          resetPage={() => setPageNumber(1)}
          maxLength={6} // e.g. max 6 digits
          min={0}
          max={999999}
        />
        <Button
          size={"sm"}
          disabled={apiFetchRef.current}
          onClick={() => fetchData(false)}
        >
          Refresh
        </Button>
        <Button
          size={"sm"}
          disabled={apiFetchRef.current}
          onClick={() => {
            setFromCode(undefined);
            setToCode(undefined);
          }}
        >
          Clear Filters
        </Button>
        <Tooltip label="Download CSV">
          <ActionIcon size={"36"} onClick={() => fetchData(true)}>
            <IconDownload size={24} />
          </ActionIcon>
        </Tooltip>
      </Group>

      <Divider label="Leave above filters blank if you want to report on all data" />
      <Group justify="space-between" wrap="nowrap" mb="md">
        <Group justify="flex-end" wrap="nowrap" mb="md">
          <Text size="sm" c="dimmed">
            Showing
          </Text>
          <Select
            value={pageSize.toString()}
            onChange={(val) => setPageSize(Number(val))}
            data={["25", "50", "100", "200"].map((v) => ({
              value: v,
              label: v,
            }))}
          />
          <Text> items per page</Text>
        </Group>
        <Pagination
          total={Math.ceil(total / pageSize)}
          value={pageNumber}
          onChange={setPageNumber}
          siblings={1}
          boundaries={1}
        />
      </Group>
      <Center>
        <Title size="lg">
          {`Products ${reportType === 0 ? "Sales Analysis" : "Monthly Averages"} Report for ${selectedYear}`}
        </Title>
      </Center>
      <Table striped highlightOnHover withTableBorder withColumnBorders>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Product</Table.Th>
            <Table.Th>Description</Table.Th>
            <Table.Th>Last</Table.Th>
            {reportType !== 0 ? (
              <>
                <Table.Th>Qtr 1</Table.Th>
                <Table.Th>Qtr 2</Table.Th>
                <Table.Th>Qtr 3</Table.Th>
                <Table.Th>Qtr 4</Table.Th>
                <Table.Th>Total</Table.Th>
                <Table.Th>Avg</Table.Th>
                <Table.Th>Ex-Avg</Table.Th>
                <Table.Th>Avg. M/S</Table.Th>
                <Table.Th>C/Ord</Table.Th>
                <Table.Th>S/Ord</Table.Th>
                <Table.Th>On Hand</Table.Th>
                <Table.Th>Months</Table.Th>
                <Table.Th>Order</Table.Th>
              </>
            ) : (
              <>
                <Table.Th>Jan</Table.Th>
                <Table.Th>Feb</Table.Th>
                <Table.Th>Mar</Table.Th>
                <Table.Th>Apr</Table.Th>
                <Table.Th>May</Table.Th>
                <Table.Th>Jun</Table.Th>
                <Table.Th>Jul</Table.Th>
                <Table.Th>Aug</Table.Th>
                <Table.Th>Sep</Table.Th>
                <Table.Th>Oct</Table.Th>
                <Table.Th>Nov</Table.Th>
                <Table.Th>Dec</Table.Th>
                <Table.Th>C/Ord</Table.Th>
                <Table.Th>S/Ord</Table.Th>
                <Table.Th>On Hand</Table.Th>
                <Table.Th>Order</Table.Th>
              </>
            )}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {loading ? (
            <Table.Tr>
              <Table.Td colSpan={reportType === 0 ? 18 : 16}>
                <Center>
                  <Loader />
                </Center>
              </Table.Td>
            </Table.Tr>
          ) : reportType === 0 ? (
            <SalesAnalysisView data={data as RptSalesAnalysisByMonths} />
          ) : (
            <SalesAveragesView data={data as RprtSalesAnalysisByQuarters} />
          )}
        </Table.Tbody>
      </Table>
    </Stack>
  );
};

export default AdminSalesAnalysisReportPage;
